import { Auth, type ProcessEventResponse } from "@saasbase-io/loginflow-websdk";
import { html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";

import { NAMESPACE } from "@/constants";
import { genRandSeed } from "@/utils";

import { SbSubmitButton } from "@/components/renderers/auth/button";

import { FIELDS, INTERACTIVES } from "./form.defs";
import type {
  Field,
  FormOnReset,
  FormOnSubmit,
  FormPart,
  Interactive,
  SbFormProps as Props,
} from "./form.types";

/**
 * Form container that orchestrates validation, collects field data, and handles
 * auth service communications.
 *
 * @part form - form: root
 *
 * @event sb-form:submit - Fired synchronously after validation passes, before the auth call.
 *  Cancelable: call `event.preventDefault()` to abort the auth call.
 *  Detail: `{ id: string; data: Record<string, string>; event: string }`
 * @event sb-form:success - Fired after the auth service call resolves successfully.
 *  Detail: `{ id: string; data: Record<string, string>; event: string; result: ProcessEventResponse }`
 * @event sb-form:error - Fired when the auth service call fails.
 *  Detail: `{ id: string; data: Record<string, string>; event: string; message: string }`
 * @event sb-form:reset - Fired when the form is reset.
 *  Detail: `{ id: string }`
 */
@customElement("sb-form")
export class SbForm extends LitElement implements Props {
  static readonly ROOT = "form";

  static readonly TAG = `${NAMESPACE}-${SbForm.ROOT}`;

  static readonly SUBMIT_EVENT = `${SbForm.TAG}:submit`;
  static readonly SUCCESS_EVENT = `${SbForm.TAG}:success`;
  static readonly ERROR_EVENT = `${SbForm.TAG}:error`;

  static readonly RESET_EVENT = `${SbForm.TAG}:reset`;

  static readonly PARTS: Record<FormPart, string> = { root: SbForm.ROOT };

  /* ------------------------------------------------------------ */

  /**
   * Substring used in generated part IDs.
   */
  @property({ type: String })
  seed = genRandSeed();

  /**
   * Custom ID used as the root part ID and as the base of subpart IDs.
   */
  @property({ type: String, attribute: "custom-id" })
  customId?: string;

  /**
   * Custom submission handler that replaces the default auth service call.
   */
  @property({ attribute: false })
  onSubmit?: FormOnSubmit;

  /**
   * Called after the native form reset event fires.
   */
  @property({ attribute: false })
  onReset?: FormOnReset;

  /* ---------------------------------------- */

  /**
   * Auth `event` captured from the most recent submit button click event.
   * Cleared after each submission attempt.
   */
  private _pendingEvent?: string;

  /**
   * Indicates that an async auth call is in-flight.
   * Guarding against double-submission.
   */
  private _loading = false;

  /* ------------------------------------------------------------ */

  protected createRenderRoot() {
    return this;
  }

  /* ------------------------------------------------------------ */

  get rootId() {
    return this.customId ?? `${SbForm.TAG}-${this.seed}`;
  }

  /* ---------------------------------------- */

  private get _form() {
    const selectors = `form[part="${SbForm.PARTS.root}"]`;
    return this.querySelector<HTMLFormElement>(selectors);
  }

  private get _fields() {
    const form = this._form;
    if (!form) return [];

    const fields = Array.from(
      form.querySelectorAll<Field>(FIELDS.map((f) => f.TAG).join(", ")),
    );

    return fields.filter((field: any) => {
      return typeof field.isFormField === "undefined"
        ? true
        : field.isFormField;
    });
  }

  private get _submit() {
    const form = this._form;
    if (!form) return null;

    const selectors = SbSubmitButton.TAG;
    return form.querySelector<SbSubmitButton>(selectors);
  }

  private get _interactives() {
    const form = this._form;
    if (!form) return [];

    const selectors = INTERACTIVES.map((i) => i.TAG).join(", ");
    return Array.from(form.querySelectorAll<Interactive>(selectors));
  }

  /* ------------------------------------------------------------ */

  connectedCallback() {
    super.connectedCallback();

    const submitClickType = SbSubmitButton.CLICK_EVENT;
    this.addEventListener(submitClickType, this._handleSubmitClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const submitClickType = SbSubmitButton.CLICK_EVENT;
    this.removeEventListener(submitClickType, this._handleSubmitClick);
  }

  protected firstUpdated() {
    const form = this._form;
    if (!form) return;

    const children = Array.from(this.childNodes).filter((n) => n !== form);
    children.forEach((n) => form.appendChild(n));
  }

  /* ------------------------------------------------------------ */

  private _dispatch(name: string, detail: Record<string, unknown> = {}) {
    this.dispatchEvent(
      new CustomEvent(name, { detail, bubbles: true, composed: true }),
    );
  }

  private _setLoading(
    loading: boolean,
    fields: Field[],
    wasDisabled: boolean[],
    interactives: Interactive[],
    wasInteractivesDisabled: boolean[],
  ) {
    // Restore pre-loading state rather than unconditionally re-enabling,
    // so fields that were already disabled before loading stay disabled after.
    fields.forEach((field, i) => {
      field.disabled = loading ? true : wasDisabled[i];
    });

    interactives.forEach((interactive, i) => {
      interactive.disabled = loading ? true : wasInteractivesDisabled[i];
    });

    const submit = this._submit;
    if (submit) submit.loading = loading;

    this._loading = loading;
  }

  /* ------------------------------------------------------------ */

  private _handleSubmitClick = (e: Event) => {
    this._pendingEvent = (e as CustomEvent<{ event?: string }>).detail?.event;
  };

  private _handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();

    if (this._loading) return;

    const fields = this._fields;

    // Use map (not every) so all fields are validated and show their errors.
    const results = fields.map((field) => field.validate());
    console.log("FIELDS", fields);

    const allValid = results.every(Boolean);
    if (!allValid) return;

    const data: Record<string, string> = {};
    fields.forEach((field) => {
      data[field.name] = field.value;
    });

    const event = this._pendingEvent;

    if (this.onSubmit) {
      this.onSubmit(data, event);
      return;
    }

    // Auth call requires an `event`, set by the preceding submit button click.
    if (!event) return;

    const submitIntent = new CustomEvent(SbForm.SUBMIT_EVENT, {
      detail: { id: this.rootId, data, event },
      bubbles: true,
      composed: true,
      cancelable: true,
    });
    this.dispatchEvent(submitIntent);
    if (submitIntent.defaultPrevented) return;

    const interactives = this._interactives;
    const wasDisabled = fields.map((f) => !!f.disabled);
    const wasInteractivesDisabled = interactives.map((i) => !!i.disabled);
    this._setLoading(
      true,
      fields,
      wasDisabled,
      interactives,
      wasInteractivesDisabled,
    );

    try {
      console.log(data);
      const result: ProcessEventResponse = await Auth.processLoginflowEvent({
        event,
        ...(Object.keys(data).length ? { data } : {}),
      } as Parameters<typeof Auth.processLoginflowEvent>[0]);

      this._dispatch(SbForm.SUCCESS_EVENT, {
        id: this.rootId,
        data,
        event,
        result,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this._dispatch(SbForm.ERROR_EVENT, {
        id: this.rootId,
        data,
        event,
        message,
      });
    } finally {
      this._setLoading(
        false,
        fields,
        wasDisabled,
        interactives,
        wasInteractivesDisabled,
      );
      this._pendingEvent = undefined;
    }
  };

  private _handleReset = (e: Event) => {
    this._pendingEvent = undefined;
    if (this.onReset) this.onReset(e);
    this._dispatch(SbForm.RESET_EVENT, { id: this.rootId });
  };

  /* ------------------------------------------------------------ */

  render() {
    return html`
      <form
        id="${this.rootId}"
        part="${SbForm.PARTS.root}"
        @submit=${this._handleSubmit}
        @reset=${this._handleReset}
      ></form>
    `;
  }
}
